import { axios, AxiosResponse } from "@sdk/axios";
import { iOrganization } from "@sdk/user-management/user-management.models";

import {
  CreateStripeLinkBody,
  CreateStripeLinkResponse,
  iStripeCustomer,
  iStripeCustomerSubscription,
  iStripeInvoice,
} from "./stripe/stripe-api-models";

export type iExternalAppType =
  // Payment Providers
  | "STRIPE"
  // Accounting
  | "QUICKBOOKS"
  // Help Desk
  | "ZENDESK"
  | "FRESH_DESK"
  | "ZOHO_DESK"
  // Team Communication
  | "SLACK";

export class ExternalAccountsServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
  }
  constructor(protected config: { basePath: string; token: string }) {}

  externalAccounts = {
    completeIntegration: async (data: {
      type: iExternalAppType;
      data: {
        code?: string;
        accessToken?: string;
        apiKey?: string;
        state?: string;
      };
    }) => {
      const results: AxiosResponse<iOrganization> = await axios.post(
        this.config.basePath + "/external-apps/complete-integration",
        data,
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
    updateConfig: async (data: { type: iExternalAppType; config: any }) => {
      const results: AxiosResponse<iOrganization> = await axios.post(
        this.config.basePath + "/external-apps/update-config",
        data,
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
    removeIntegration: async (data: { type: iExternalAppType }) => {
      const results: AxiosResponse<iOrganization> = await axios.post(
        this.config.basePath + "/external-apps/remove-integration",
        data,
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
  };

  stripe = {
    searchCustomers: async (req: {
      name?: string;
      email?: string;
      phone?: string;
    }) => {
      const results: AxiosResponse<iStripeCustomer[]> = await axios.post(
        this.config.basePath + "/stripe/search-customers",
        req,
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
    getCustomerById: async (req: { customerId: string }) => {
      const results: AxiosResponse<iStripeCustomer> = await axios.post(
        this.config.basePath + "/stripe/get-customer-by-id",
        req,
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
    getCustomerSubscriptions: async (req: { customerId: string }) => {
      const results: AxiosResponse<{ data: iStripeCustomerSubscription[] }> =
        await axios.post(
          this.config.basePath + "/stripe/get-customer-subscription",
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
      return results.data;
    },
    getCustomerInvoices: async (req: { customerId: string }) => {
      const results: AxiosResponse<{ data: iStripeInvoice[] }> =
        await axios.post(
          this.config.basePath + "/stripe/get-customer-invoices",
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
      return results.data;
    },
    createPaymentLink: async (body: CreateStripeLinkBody) => {
      const results: AxiosResponse<CreateStripeLinkResponse> =
        await axios.post(
          this.config.basePath + "/stripe/create-payment-link",
          body,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
      return results.data;
    },
    createQuoteDepositLink: async (body: {
      quoteId: string;
      pricingBlockId: string;
      successUrl: string;
      cancelUrl: string;
      customerEmail?: string;
    }) => {
      const results: AxiosResponse<CreateStripeLinkResponse> =
        await axios.post(
          this.config.basePath + "/stripe/create-quote-deposit-link",
          body,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
      return results.data;
    },
  };

  quickbooks = {
    startOAuth: async (): Promise<{ url: string }> => {
      const results: AxiosResponse<{ url: string }> = await axios.get(
        this.config.basePath + "/quickbooks/oauth/start",
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
    getAccounts: async (): Promise<{ accounts: any[] }> => {
      const results: AxiosResponse<{ accounts: any[] }> = await axios.get(
        this.config.basePath + "/quickbooks/accounts",
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
    updateConfig: async (
      body: Partial<{
        syncEnabled?: boolean;
        defaultIncomeAccountId?: string;
        defaultIncomeAccountName?: string;
        defaultExpenseAccountId?: string;
        defaultExpenseAccountName?: string;
      }>
    ): Promise<{ config: Record<string, unknown> }> => {
      const results: AxiosResponse<{ config: Record<string, unknown> }> =
        await axios.patch(this.config.basePath + "/quickbooks/config", body, {
          headers: {
            Authorization: this.config.token,
          },
        });
      return results.data;
    },
    resyncTransaction: async (
      transactionId: string
    ): Promise<{ transaction: Record<string, unknown> }> => {
      const results: AxiosResponse<{ transaction: Record<string, unknown> }> =
        await axios.post(
          `${this.config.basePath}/quickbooks/resync-transaction/${encodeURIComponent(transactionId)}`,
          {},
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
      return results.data;
    },
    disconnect: async (): Promise<{ disconnected: true }> => {
      const results: AxiosResponse<{ disconnected: true }> = await axios.post(
        `${this.config.basePath}/quickbooks/disconnect`,
        {},
        {
          headers: {
            Authorization: this.config.token,
          },
        }
      );
      return results.data;
    },
  };
}
